// API Environment Configuration
// Set REACT_APP_API_ENV in .env file
// Options: local, development, staging, production
const API_ENV = process.env.REACT_APP_API_ENV || 'local';

// API Host URLs
//
// Each brand frontend calls its OWN brand API hostname so the X-Brand
// response header + session cookies + CORS origin all match cleanly.
// The Laravel backend is shared across all 3 brands (same DB, same code,
// same secrets) but answers on per-brand hostnames via DetectBrand middleware.
//
// Dev, Stg, AND Prod are now brand-specific. Infra timeline:
//   - dev:  nginx + multi-SAN LE cert on 52.70.31.54 — 2026-04-09
//   - stg:  nginx + multi-SAN LE cert on 13.218.74.36 — 2026-04-10
//   - prod: nginx + multi-SAN LE cert on 23.20.219.187 — 2026-04-10
const API_HOSTS = {
  local: 'https://api.lickly.test/api/v1',

  // Development — brand-specific (nginx + multi-SAN cert on 52.70.31.54)
  development: 'https://api-dev.lickly.com/api/v1',
  development_radintel: 'https://api-dev.radintel.ai/api/v1',
  development_radamplify: 'https://api-dev.radamplify.com/api/v1',

  // Staging — brand-specific (nginx + multi-SAN cert on 13.218.74.36)
  staging: 'https://api-stg.lickly.com/api/v1',
  staging_radintel: 'https://api-stg.radintel.ai/api/v1',
  staging_radamplify: 'https://api-stg.radamplify.com/api/v1',

  // Production — brand-specific (nginx + multi-SAN cert on 23.20.219.187)
  production: 'https://api.lickly.com/api/v1',
  production_radintel: 'https://api.radintel.ai/api/v1',
  production_radamplify: 'https://api.radamplify.com/api/v1',

  // RADzilla instance (AAPU-6901) — radzilla-base branch, dev DB + dev ML.
  // One Laravel app serving both brand hostnames, so each brand UI calls its own
  // API host and CORS / cookies / X-Brand stay aligned.
  radzilla_radintel: 'https://api-radzilla.radintel.ai/api/v1',
  radzilla_lickly: 'https://api-radzilla.lickly.com/api/v1',

  // Dev NewUI sandbox backend (workspace feature — separate cloned RDS cluster)
  demo_radintel: 'https://api-demo.radintel.ai/api/v1',
  demo_radamplify: 'https://api-demo.radamplify.com/api/v1',
  demo_lickly: 'https://api-demo.lickly.com/api/v1',
};

// Environment names
const ENV_NAMES = {
  local: 'LOCAL',
  development: 'DEVELOPMENT',
  staging: 'STAGING',
  production: 'PRODUCTION',
  apiDemo: 'API_DEMO',
  radzilla: 'RADZILLA',
};

// Set API_HOST and ENV based on environment variable
/* eslint-disable import/no-mutable-exports */
let API_HOST = API_HOSTS[API_ENV] || API_HOSTS.local;
let ENV = ENV_NAMES[API_ENV] || 'LOCAL';
/* eslint-enable import/no-mutable-exports */

// Sandbox (per-hire, all 3 brands) — derive the API host from the sbx.<brand>
// hostname so each brand UI calls its own api-<host> (X-Brand / cookies / CORS
// all align). Runs in the dev-server bundle too, unlike the production switch
// below. radintel uses <hire>.sbx.radintel.ai; lickly/radamplify use the
// first-level <hire>-sbx.<brand> form (Universal SSL covers one label), so the
// pattern accepts both a dot and a dash before "sbx".
if (
  typeof window !== 'undefined' &&
  window.location &&
  /[.-]sbx\.(radintel\.ai|lickly\.com|radamplify\.com)$/i.test(window.location.hostname)
) {
  API_HOST = `${window.location.protocol}//api-${window.location.hostname}/api/v1`;
  ENV = 'SANDBOX';
}

// Deployed frontend hostname -> the brand API it calls and its env name.
// A single React bundle is deployed to all 3 brand frontend hostnames, and at
// runtime we look up window.location.hostname here so a user on dev.radintel.ai
// calls api-dev.radintel.ai (not dev.lickly.com's backend), which keeps
// CORS / Sanctum stateful domains / cookies / X-Brand header all aligned.
// This map is also the canonical list of our own deployed frontend hostnames
// (src/util/appOrigins.js builds the trusted-origin allowlist from it).
export const FRONTEND_HOSTS = {
  // --- Development ---
  'dev.lickly.com': { apiHost: API_HOSTS.development, env: 'DEVELOPMENT' },
  'dev-ui.lickly.com': { apiHost: API_HOSTS.development, env: 'DEVELOPMENT' },
  'dev.radintel.ai': { apiHost: API_HOSTS.development_radintel, env: 'DEVELOPMENT' },
  'dev.radamplify.com': { apiHost: API_HOSTS.development_radamplify, env: 'DEVELOPMENT' },

  // Dev NewUI - workspace feature sandbox (points at api-demo backend)
  'dev-newui.radintel.ai': { apiHost: API_HOSTS.demo_radintel, env: 'API_DEMO' },
  'dev-newui.lickly.com': { apiHost: API_HOSTS.demo_lickly, env: 'API_DEMO' },
  'dev-newui.radamplify.com': { apiHost: API_HOSTS.demo_radamplify, env: 'API_DEMO' },

  // --- RADzilla (AAPU-6901) ---
  'radzilla.radintel.ai': { apiHost: API_HOSTS.radzilla_radintel, env: 'RADZILLA' },
  'radzilla.lickly.com': { apiHost: API_HOSTS.radzilla_lickly, env: 'RADZILLA' },

  // --- Staging (brand-specific since 2026-04-10) ---
  'stg.lickly.com': { apiHost: API_HOSTS.staging, env: 'STAGING' },
  'stg.radintel.ai': { apiHost: API_HOSTS.staging_radintel, env: 'STAGING' },
  'stg.radamplify.com': { apiHost: API_HOSTS.staging_radamplify, env: 'STAGING' },

  // --- Production (brand-specific since 2026-04-10) ---
  'app.lickly.com': { apiHost: API_HOSTS.production, env: 'PRODUCTION' },
  'app.radintel.ai': { apiHost: API_HOSTS.production_radintel, env: 'PRODUCTION' },
  'app.radamplify.com': { apiHost: API_HOSTS.production_radamplify, env: 'PRODUCTION' },
};

// Override with hostname for deployed environments (production builds).
// Other hostnames keep their .env settings.
if (
  process.env.NODE_ENV === 'production' &&
  Object.prototype.hasOwnProperty.call(FRONTEND_HOSTS, window.location.hostname)
) {
  ({ apiHost: API_HOST, env: ENV } = FRONTEND_HOSTS[window.location.hostname]);
}

export { API_HOST, ENV };

// OAuth 2.0 endpoints (new - recommended)
export const OAUTH2_TOKEN_URL = `${API_HOST}/oauth2/token`;
export const OAUTH2_REVOKE_URL = `${API_HOST}/oauth2/revoke`;
export const OAUTH2_EXCHANGE_CODE_URL = `${API_HOST}/oauth2/exchange-code`;

// Legacy OAuth 1.0 endpoints (deprecated)
export const AUTHENTICATION_URL = `${API_HOST}/api/get-tokens/`;
export const ACP_AUTHENTICATION_URL = `${API_HOST}/api/acp-login/`;
export const SIGNUP_URL = `${API_HOST}/account/create/`;

/** SIGN UP END POINTS */
export const REGISTER_SEND_CODE_URL = `${API_HOST}/auth/register/send-code`;
export const REGISTER_VERIFY_CODE_URL = `${API_HOST}/auth/register/verify-code`;
export const REGISTER_RESEND_CODE_URL = `${API_HOST}/auth/register/resend-code`;
export const REGISTER_COMPLETE_URL = `${API_HOST}/auth/register/complete`;

export const REGISTER_SEND_LINK_URL = `${API_HOST}/auth/register/send-link`;
export const REGISTER_RESEND_LINK_URL = `${API_HOST}/auth/register/resend-link`;
export const REGISTER_FROM_HUBSPOT_URL = `${API_HOST}/auth/register/from-hubspot`;

/** REQUEST TO JOIN WORKSPACE (public self-service, RJ01–RJ03) */
export const REQUEST_TO_JOIN_SEND_CODE_URL = `${API_HOST}/auth/request-to-join/send-code`;
export const REQUEST_TO_JOIN_SEND_LINK_URL = `${API_HOST}/auth/request-to-join/send-link`;
export const REQUEST_TO_JOIN_VERIFY_CODE_URL = `${API_HOST}/auth/request-to-join/verify-code`;
export const REQUEST_TO_JOIN_SUBMIT_URL = `${API_HOST}/auth/request-to-join`;

/** SIGN UP INVITATION END POINTS */
export const REGISTER_LOAD_INVITATION_URL = `${API_HOST}/:workspaceName/invitations/:invitationToken`;
export const REGISTER_INVITATION_ACCEPT_URL = `${API_HOST}/:workspaceName/invitations/:invitationToken/accept`;
export const REGISTER_INVITATION_DECLINE_URL = `${API_HOST}/:workspaceName/invitations/:invitationToken/decline`;

/** Sign In (Fortify) */
export const AUTH_LOGIN_URL = `${API_HOST}/auth/login`;

/** WORKSPACE END POINTS */
export const AUTH_ME_URL = '/auth/me';

/* EMAIL END POINTS */
export const EMAIL_DATA_CLIENT_URL = '/email-data/getEmailData';
export const EMAIL_CONTENT_CLIENT_URL = '/email-data/getEmailContent';
export const EMAIL_DATA_MEASURES_CLIENT_URL = '/email-data/getEmailMeasuresData';
export const EMAIL_DATA_UPDATE = '/email-data/updateEmailData';

/* BLOG POSTS END POINTS */
export const BLOG_POSTS_DATA_CLIENT_URL = '/posts/getAllPosts';
export const BLOG_POSTS_DATA_MEASURES_CLIENT_URL = '/posts/getPostsMeasuresData';
export const BLOG_POSTS_CONTENT_AR = '/posts/getPostById';
export const BLOG_POSTS_CONTENT_HUBSPOT = '/Hubspot/getBlogPost'; // "blogPostId"=>$postId
export const HUBSPOT_BLOG_INSTACES_LIST = '/Hubspot/getBlogs';
export const GET_GOAL_NAMES = '/Content/getGoals';
export const CREATE_NEW_HUBSPOT_POST = '/Hubspot/createNewBlogPost';

/* ADS END POINTS */
export const ADS_DATA_CLIENT_URL = '/adwords/get-ads-v2';
export const ADS_FOR_CAMPAIGNS_ADGROUPS_DATA_CLIENT_URL =
  '/adwords/get-ads-for-campaigns-and-adgroups';

export const GET_AD_PROFILE_SELECTION = '/adwords/get-ads-selection-data';

export const ADWORDS_TOTALS = '/adwords/get-ads-totals';
export const CAMPAIGNS_DATA_CLIENT_URL = '/adwords/get-campaigns';
export const ADGROUP_DATA_CLIENT_URL = '/adwords/get-ad-groups';
export const ADGROUP_CAMPAIGN_ID_DATA_CLIENT_URL = '/adwords/get-ad-groups-by-campaign-id';
export const GET_AD_BY_ID_URL = '/adwords/get-ad-by-id';
export const GET_AD_DRAFT_BY_ID_URL = '/editor-drafts/getDraftAd';
export const GET_AD_MEASURES_BY_ID = '/adwords/get-ads-measures-data';
export const SAVE_ADS_DRAFT_URL = '/editor-drafts/saveDraftsAds';
export const DELETE_ADS_DRAFT_URL = '/editor-drafts/deleteDraftsAds';
export const PERS_REQUEST1_URL = '/content/rewrite-diff';
export const PERS_REQUEST2_URL = '/content/merge-html-text';

export const SERVICE_GET_SUMMARY = '/service/getSummary/';

export const GET_ALL_BATCH = '/batch/get-batches';
export const DELETE_BATCHES = '/batch/delete-batch-content';
export const SAVE_BATCH = '/batch/save-batch-content';
export const GET_ADS_BATCH = '/batch/get-batch-by-id';
export const UPDATE_BATCH_CONTENT = '/batch/update-batch-content';
export const UPDATE_BATCH_PROFILE = '/batch/update-batch-profile';
export const REMOVE_BATCH_CONTENT = '/batch/remove-batch-content';

/* DRAFTS END POINTS */
export const DRAFTS_DATA_CLIENT_URL = '/editor-drafts/getDrafts';

/* Fetching All Drafts from accountId
 * Param: No Parameter
 * The Endpoint doesn't accept no parameters
 * */
export const POST_DRAFTS_DATA_CLIENT_URL = '/posts/getAllDrafts';
export const DRAFT_DATA_CLIENT_URL = '/editor-drafts/getDraft';
export const SAVE_DRAFT_DATA_CLIENT_URL = '/editor-drafts/saveDrafts';
export const UPDATE_DRAFT_STATUS_CLIENT_URL = '/editor-drafts/updateDraftStatus';
export const DELETE_DRAFTS = '/editor-drafts/deleteDrafts';

export const SAVE_HUBSPOT_EMAIL_DATA_CLIENT_URL = '/Hubspot/updateEmail';
export const SAVE_HUBSPOT_BLOG_DATA_CLIENT_URL = '/Hubspot/updateBlogPost';

export const TOPICS_RELATED_CLUSTERS_URL = '/topic/get-insights';
export const TOPICS_RELATED_CLUSTERS_URL_V2 = '/topic/getInsightsV2';

/* PROFILE END POINTS */
export const PROFILE_DATA_CLIENT_URL = '/scoring-profile/getAllProfiles';
export const PROFILE_DATA_CLIENT_LITE_URL = '/scoring-profile/getAllProfilesLite';
export const PROFILE_MEASURES = '/scoring-profile/getScoringProfileDetails';
export const PROFILE_BUCKET_CORPUS = '/scoring-profile/getScoringProfileMeasureCorpus';
export const PROFILE_CONTENT = '/scoring-profile/getScoringProfileCorpus';
export const PROFILE_CREATE = '/scoring-profile/createScoringProfile';
export const ADD_CONTENTIDS = '/scoring-profile/addCorpusElements';
export const REMOVE_CONTENTIDS = '/scoring-profile/deleteCorpusElements';
export const PROFILES_DELETE = '/scoring-profile/deleteScoringProfiles';
export const PROFILES_ACTIVATE = '/scoring-profile/undoDeleteScoringProfiles';
export const PROFILE_DELETE = '/scoring-profile/deleteScoringProfile';
export const PROFILE_ACTIVATE = '/scoring-profile/undoDeleteScoringProfile';
export const GET_STANDARD_PROFILE = '/scoring-profile/getStandardProfile';

export const PROFILE_CALCULATE = '/scoring-profile/calculate';
export const PROFILE_CALCULATE_CUTOFF = '/scoring-profile/calculateCutOffScore';
export const PROFILE_UPDATE = '/scoring-profile/updateScoringProfileName';

/* SCRAPE ARTICLE */
export const SCRAPE_ARTICLE_URL = '/scrape/article'; // "postId"=>$postId

/* ACCOUNT SETTINGS */

export const ACCOUNT_FORGOT_PASSWORD = '/account/forgot-password';
export const ACCOUNT_RECOVER_PASSWORD = '/account/recover-password';
export const ACCOUNT_CHANGE_PASSWORD = '/account/change-password';
export const ACCOUNT_UPDATE_DETAILS = '/account/update-account-details';
export const ACCOUNT_REQUEST_TERMINATION = '/account/request-termination';

export const GET_WORD_LIST = '/:list/list';
export const DELETE_WORD = '/:list/remove';
export const ADD_WORD = '/:list/add';

/* Sub Accounts */
export const GET_SUB_ACCOUNTS = '/partner/sub-accounts';
export const ADD_SUB_ACCOUNTS = '/partner/create-sub-account';
export const REMOVE_SUB_ACCOUNTS = '/partner/remove-sub-account';
export const UPDATE_SUB_ACCOUNT = '/partner/update-sub-account';

/* INTEGRATIONS */

export const CHECK_HUBSPOT = '/hubspot/check-connection';
export const HUBSPOT_AUTH_LINK = '/hubspot/get-auth-link';
export const HUBSPOT_CONNECT = '/hubspot/connect-account';
export const HUBSPOT_DISCONNECT = '/hubspot/disconnect';
export const HUBSPOT_FORM_SUBMIT = '/hubspot/form-submit';
export const HUBSPOT_SEND_LINK_URL = `${API_HOST}/hubspot/send-link`;
export const HUBSPOT_IMPORT_DATA = '/Hubspot/uploadMetrics';

// Gmail outreach integration (AAPU-5599). OAuth params come from the backend;
// connect exchanges the Google auth code. Disconnect reuses the generic
// /outreach/removeOAuthConnection endpoint (same as Outlook).
export const GMAIL_OAUTH_CONFIG_URL = '/gmail/oauth-config';
export const GMAIL_CONNECT_URL = '/gmail/connect';

export const CHECK_MAILCHIMP = '/mail-chimp/isConnected';
export const MAILCHIMP_CONNECT = '/mail-chimp/connect';
export const MAILCHIMP_AUTH_LINK = '/mail-chimp/get-auth-link';
export const MAILCHIMP_DISCONNECT = '/mail-chimp/disconnect';

// export const CHECK_WORDPRESS = '/wordpress/check-connection';
export const CHECK_WORDPRESS = '/wordpress/getAllConnections';
export const WORDPRESS_DISCONNECT = '/wordpress/disconnect';
export const WORDPRESS_DISCONNECT_BY_SOURCE_ID = '/wordpress/disconnectBySourceId';
export const WORDPRESS_UPDATE_POSTS = '/wordpress/update-posts';
export const CREATE_NEW_WP_POST = '/wordpress/create-new-post';
export const BLOG_POSTS_CONTENT_WORDPRESS = '/wordpress/get-post-by-idv2'; // "postId"=>$postId

export const GET_CONTENT_LIST = '/source/list';
export const GET_CONTENT_URL = '/source/add-v2';
export const ADD_NEW_ARTICLE_URL = '/posts/addPostByUrl';
export const GET_SEARCH_CONSOLE_PROPERTIES_LIST = '/search-console/getProperties';

export const GET_GA_PROFILES = '/account/ga-profiles2';
export const UPDATE_GA_PROFILE = '/source/update';
export const ADD_GA_PROFILE = '/account/add-social-network';

export const IS_MARKETO_CONNECTED = '/marketo/isConnected';
export const MARKETO_CONNECT = '/marketo/connect';
export const MARKETO_GET_FOLDERS = '/marketo/getFolders';
export const MARKETO_DISCONNECT = '/marketo/disconnect';

export const MARKETO_UPDATE_CONFIGURATION = '/marketo/updateConfiguration';
export const CHECK_ADWORDS_CONNECTED = '/adwords/checkMultiConnection';
export const ADWORDS_CONNECT = '/adwords/connect-from-app';
export const ADWORDS_AUTH_LINKS = '/adwords/getAuthFromApp';
export const ADWORDS_DEFAULT_ACCOUNT = '/adwords/save-default-customer';
export const ADWORDS_ACTIVATE_CONNECTIONID = '/adwords/activateConnectionId';
export const ADWORDS_DISCONNECT_CONNECTIONID = '/adwords/disconnectConnectionId';
export const ADWORDS_DISCONNECT = '/adwords/disconnect';

// export const MARKETO_DISCONNECT = '/marketo/disconnect';

// logging
export const LOG_EVENT = `${API_HOST}/logging/log`;

export const SEARCHCONSOLE_AUTH_LINKS = '/search-console/getAuthFromApp';
export const SEARCHCONSOLE_CONNECT = '/search-console/connect-from-app';
export const SET_PROPERTY_TO_SOURCE = '/search-console/setProperty';

export const GOOGLE_ANALYTICS_AUTH_LINKS = '/analytics/getAuthFromApp';
export const GOOGLE_ANALYTICS_CONNECT = '/analytics/connectFromApp';

export const CHARGIFY_SUBSCRIPTION_VALIDATION = '/account/chargifySubscriptionValidation';

/** Signup plan options */
export const AVAILABLE_PLANS = [
  { slug: 'free-trial', label: 'Free Trial' },
  { slug: 'growth', label: 'Growth' },
  { slug: 'performance', label: 'Performance' },
  { slug: 'advanced', label: 'Advanced' },
];
export const DEFAULT_PLAN = 'free-trial';

/* CREATOR CONTENT REVISION (Public) */
export const CUSTOM_GET_CONVERSATION = '/custom/get-conversation';
export const CUSTOM_ADD_DRAFT = '/custom/add-draft';
export const CUSTOM_ADD_DRAFT_COMMENT = '/custom/add-draft-comment';
export const CUSTOM_ADD_MESSAGE = '/custom/add-message';
export const CUSTOM_UPLOAD = '/custom/upload';
export const CUSTOM_FETCH_REVISIONS = '/custom/fetch';
export const CUSTOM_FETCH_DOCUMENTS = '/custom/fetch-documents';
export const CUSTOM_DELETE_DRAFT = '/custom/delete-draft';

/* MEDIA OPTIMIZATION */
export const MEDIA_MODEL_FETCH = '/media-model/fetch-all';
export const MEDIA_MODEL_FETCH_BY_MEDIA = '/media-model/fetch-by-media';
export const MEDIA_MODEL_DELETE = '/media-model/delete';
export const MEDIA_MODEL_REANALYZE = '/media-model/re-analyze';
